import React, { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { styled } from "@mui/system";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { toast } from "react-toastify";
import {
  ExternalIcon,
  OU,
  OUTREACH_SANS,
  OuControl,
  OuEmptyState,
  OuIconButton,
  OuNotice,
  OuPage,
  OuPill,
  OuPrimaryButton,
  OuResultCount,
  OuSecondaryButton,
  OuSelect,
  OuTable,
  OuTableColumn,
  OuTitle,
  OuTone,
  OuTooltip,
  RefreshIcon,
  useCrm,
  useCrmName,
} from "../../components/CrmUi";
import { HubSpotSettingsPage } from "../../components/Layout/HubSpotAppShell";
import { HsDateRangeSelect, HsTableTopBar } from "../../components/HubSpotUi";
import Dialog from "../../components/Display/Dialog";
import Card from "../../components/Display/Card";
import PlanUpgradePrompt from "../../components/Billing/PlanUpgradePrompt";
import { ButtonVariants } from "../../components/Display/Button";
import { listJobChanges, resolveJobChanges } from "../../services/firebase";
import { jobChangesPendingAtom, signedInUserAtom } from "../../state/atoms";
import { JobChangeAction, JobChangeRow, JobChangeRowResult, JobChangeScope, JobChangeStatus, JobChangesResult } from "../../types/jobChanges";

/**
 * The Job changes page: the people in the CRM boring saw change jobs on
 * LinkedIn, in place of a review task per record. Organizations that confirm
 * job changes by hand approve (apply, exactly as the extension's dialog does)
 * or reject (dismiss for good) them here, one at a time or in bulk; ones
 * where boring applies them itself see the log of what it changed plus the
 * moves it could not apply. Reps see the records they own (any owner of a
 * multi-select owner property), admins the team. There is no undo: an
 * applied change has already rewritten the record or made a new one.
 * Backend: listJobChanges / resolveJobChanges (functions/src/job-changes.ts).
 */

type StatusFilter = JobChangeStatus | "all";

const PAGE_SIZE = 50;
/** Keys per resolveJobChanges call, so a big batch never runs into the function's timeout. */
const CHUNK = 50;

const STATUS_LABELS: Record<JobChangeStatus, string> = {
  pending: "Pending",
  applied: "Applied",
  rejected: "Rejected",
  superseded: "Replaced",
};

const STATUS_TONES: Record<JobChangeStatus, OuTone> = {
  pending: "caution",
  applied: "success",
  rejected: "neutral",
  superseded: "neutral",
};

const dayStart = (d: string) => (d ? new Date(`${d}T00:00:00`).getTime() : null);
const dayEnd = (d: string) => (d ? new Date(`${d}T23:59:59.999`).getTime() : null);
const formatDate = (ms: number) => new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
const formatDateTime = (ms: number) => new Date(ms).toLocaleString(undefined, { month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
const badgeText = (n: number) => (n > 99 ? "99+" : String(n));

const ownersOf = (r: JobChangeRow): string[] => (r.ownerEmails && r.ownerEmails.length ? r.ownerEmails : r.ownerEmail ? [r.ownerEmail] : []);

/* ── the pending count, for the nav badge ── */

/** Pending job changes in the user's default scope (theirs, or the team's for admins); 0 when unknown. */
export const useJobChangesPending = (): number => {
  const [count, setCount] = useRecoilState(jobChangesPendingAtom);
  useEffect(() => {
    if (count !== null) return;
    let alive = true;
    listJobChanges({ status: "pending", pageSize: 1 })
      .then((r) => alive && setCount(r.planRequired ? 0 : r.pendingCount))
      .catch(() => alive && setCount(0));
    return () => {
      alive = false;
    };
  }, [count, setCount]);
  return count || 0;
};

const TabBadge = styled("span")`
  display: inline-block;
  min-width: 20px;
  height: 20px;
  margin-left: 8px;
  padding: 0 6px;
  border-radius: 10px;
  background: #5e5eaf;
  color: #ffffff;
  font: 600 12px/20px ${OUTREACH_SANS};
  text-align: center;
  vertical-align: middle;
`;

/** The dashboard tab's label (Outreach orgs): "Job changes" and the pending count. */
export const JobChangesTabLabel: FC = () => {
  const count = useJobChangesPending();
  return (
    <span>
      Job changes
      {count > 0 && <TabBadge aria-label={`${count} pending`}>{badgeText(count)}</TabBadge>}
    </span>
  );
};

/** HubSpot's navigation column badge. */
export const JobChangesNavBadge: FC = () => {
  const count = useJobChangesPending();
  return count > 0 ? (
    <span className="hs-sidenav__badge" aria-label={`${count} pending`}>
      {badgeText(count)}
    </span>
  ) : null;
};

/* ── cells ── */

const Cell: FC<{ value: ReactNode; title?: string | null }> = ({ value, title }) => (
  <div className="ou-cell hs-cell" title={title || (typeof value === "string" ? value : undefined)}>
    {value}
  </div>
);

const Primary = styled("div")`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font: 600 14px/20px ${OUTREACH_SANS};
  color: ${OU.ink};

  a {
    color: ${OU.ink};
    font-weight: 600;
  }
  a:hover {
    color: ${OU.brand};
  }
`;

const Secondary = styled("div")`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font: 400 14px/20px ${OUTREACH_SANS};
  color: ${OU.muted};

  && a {
    font-weight: 400;
  }
`;

const PersonCell: FC<{ row: JobChangeRow; crmName: string }> = ({ row, crmName }) => {
  const name = row.name || `Record ${row.recordId}`;
  return (
    <div style={{ minWidth: 0 }}>
      <Primary title={name}>
        {row.recordUrl ? (
          <a href={row.recordUrl} target="_blank" rel="noopener noreferrer" title={`${name} in ${crmName}`}>
            {name}
          </a>
        ) : (
          name
        )}
      </Primary>
      {row.linkedInUrl && (
        <Secondary>
          <a href={row.linkedInUrl} target="_blank" rel="noopener noreferrer">
            LinkedIn
            <ExternalIcon />
          </a>
        </Secondary>
      )}
    </div>
  );
};

const RoleCell: FC<{ title: string | null; company: string | null }> = ({ title, company }) => (
  <div style={{ minWidth: 0 }}>
    <Primary style={{ fontWeight: 400 }} title={company || undefined}>
      {company || "—"}
    </Primary>
    {title && <Secondary title={title}>{title}</Secondary>}
  </div>
);

const OwnerCell: FC<{ row: JobChangeRow }> = ({ row }) => {
  const owners = ownersOf(row);
  if (!owners.length) return <Cell value={<span style={{ color: OU.muted }}>No owner</span>} />;
  return (
    <div className="ou-cell hs-cell" title={owners.join(", ")}>
      {owners[0]}
      {owners.length > 1 && <span style={{ color: OU.muted }}>{` +${owners.length - 1}`}</span>}
    </div>
  );
};

const StatusCell: FC<{ row: JobChangeRow }> = ({ row }) => {
  const label = row.status === "applied" && row.auto ? "Applied by boring" : STATUS_LABELS[row.status] || row.status;
  const when = row.status === "applied" ? row.appliedAt : row.status === "rejected" ? row.rejectedAt : null;
  return (
    <OuPill tone={STATUS_TONES[row.status] || "neutral"} title={when ? `${label} ${formatDateTime(when)}` : undefined}>
      {label}
    </OuPill>
  );
};

const OUTCOME_LABELS: Record<string, string> = {
  updated: "Record updated",
  closed_and_created: "Closed out, new record made",
  closedAndCreated: "Closed out, new record made",
  created: "New record made",
  closed: "Closed out",
};

/** What the last action did: this session's result first, else what the row recorded. */
const ResultCell: FC<{ row: JobChangeRow; result?: JobChangeRowResult; crmName: string }> = ({ row, result, crmName }) => {
  const failed = result ? !result.ok : row.status === "pending" && !!row.lastError;
  const text = result
    ? result.message || (result.ok ? (result.status === "rejected" ? "Rejected" : OUTCOME_LABELS[result.outcome || ""] || "Applied") : "Not applied")
    : row.status === "pending"
    ? row.lastError || ""
    : row.status === "applied"
    ? row.message || OUTCOME_LABELS[row.outcome || ""] || ""
    : row.status === "superseded"
    ? "A newer job change replaced this one"
    : "";
  const link = result?.newRecordUrl || (row.status === "applied" ? row.newRecordUrl : null);
  if (!text && !link) return null;
  return (
    <div
      className="ou-cell hs-cell"
      title={text || undefined}
      // A reason is a sentence: it wraps instead of trailing off.
      style={{ whiteSpace: "normal", overflow: "visible", textOverflow: "clip", ...(failed ? { color: "var(--ou-danger-fg, #b42318)" } : {}) }}
    >
      {text}
      {link && (
        <>
          {text ? " · " : ""}
          <a href={link} target="_blank" rel="noopener noreferrer">
            New record in {crmName}
            <ExternalIcon />
          </a>
        </>
      )}
    </div>
  );
};

const RowActions = styled("div")`
  display: flex;
  gap: 8px;
  align-items: center;

  > * {
    flex: 0 0 auto;
    min-width: 0;
    white-space: nowrap;
  }
`;

/* ── layout ── */

const Toolbar = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px 16px;
`;

const ToolbarSide = styled("div")`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
`;

const SelectBox = styled(OuControl)`
  width: 150px;
`;

const DateInput = styled("input")`
  /* The selects' height: 40px in Outreach, 42px in HubSpot. */
  height: 40px;
  .hs-jobchanges & {
    height: 42px;
  }
  box-sizing: border-box;
  padding: 0 10px;
  border: 1px solid ${OU.inputBorder};
  border-radius: 4px;
  background: ${OU.surface};
  color: ${OU.ink};

  &:hover {
    border-color: ${OU.inputBorderHover};
  }
  font: 400 14px/20px ${OUTREACH_SANS};
`;

const DateRangeBox = styled("div")`
  width: 260px;
`;

const Caption = styled("p")`
  margin: 0;
  font: 400 14px/20px ${OUTREACH_SANS};
  color: ${OU.muted};
`;

const BulkBar = styled("div")`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  padding: 10px 12px;
  border: 1px solid ${OU.rule};
  border-radius: 6px;
  background: ${OU.soft};
  font: 400 14px/20px ${OUTREACH_SANS};
  color: ${OU.ink};
`;

const Pager = styled("div")`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  font: 400 14px/20px ${OUTREACH_SANS};
  color: ${OU.muted};
`;

const Stack = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const MoveIcon: FC = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M10 4h4a2 2 0 0 1 2 2v1h3a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h3V6a2 2 0 0 1 2-2Zm0 3h4V6h-4v1Zm2 3-1.4 1.4 1.6 1.6H8v2h4.2l-1.6 1.6L12 18l4-4-4-4Z" />
  </svg>
);

/** The kit's page is a settings column; this table wants the whole width. */
const WIDE = { maxWidth: "none" };

/* ── the page ── */

const JobChanges: FC = () => {
  const crm = useCrm();
  const crmName = useCrmName();
  const me = String(useRecoilValue(signedInUserAtom)?.email || "").trim().toLowerCase();
  const setPending = useSetRecoilState(jobChangesPendingAtom);

  const [scope, setScope] = useState<JobChangeScope | null>(null);
  const [status, setStatus] = useState<StatusFilter>("pending");
  const statusTouched = useRef(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(0);

  const [data, setData] = useState<JobChangesResult | null>(null);
  const [rows, setRows] = useState<JobChangeRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [results, setResults] = useState<Record<string, JobChangeRowResult>>({});
  const [busy, setBusy] = useState<Set<string>>(new Set());
  const [confirmApprove, setConfirmApprove] = useState<string[] | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const r = await listJobChanges({ scope: scope || undefined, status, from: dayStart(from), to: dayEnd(to), page, pageSize: PAGE_SIZE });
      // An organization where boring applies job changes itself opens on the whole log.
      if (!statusTouched.current && r.settings?.autoApply && status === "pending") {
        statusTouched.current = true;
        setStatus("all");
        return;
      }
      statusTouched.current = true;
      setData(r);
      setRows(r.rows);
      setSelected(new Set());
      setResults({});
      if (r.scope === (r.isAdmin ? "team" : "mine")) setPending(r.planRequired ? 0 : r.pendingCount);
    } catch (e: any) {
      setLoadError(e?.message || "Could not load job changes.");
    } finally {
      setIsLoading(false);
    }
  }, [scope, status, from, to, page, setPending]);

  useEffect(() => {
    load();
  }, [load]);

  const isAdmin = !!data?.isAdmin;
  const autoApply = !!data?.settings?.autoApply;
  const mayAct = useCallback((r: JobChangeRow) => isAdmin || (!!me && ownersOf(r).includes(me)), [isAdmin, me]);
  const actionable = useCallback((r: JobChangeRow) => r.status === "pending" && mayAct(r) && !busy.has(r.key), [mayAct, busy]);

  const selectedActionable = useMemo(() => rows.filter((r) => selected.has(r.key) && actionable(r)).map((r) => r.key), [rows, selected, actionable]);
  const selectedOther = selected.size - selectedActionable.length;

  const run = async (keys: string[], action: JobChangeAction) => {
    if (!keys.length) return;
    setBusy((prev) => new Set([...Array.from(prev), ...keys]));
    const all: JobChangeRowResult[] = [];
    try {
      for (let i = 0; i < keys.length; i += CHUNK) {
        const r = await resolveJobChanges(keys.slice(i, i + CHUNK), action);
        all.push(...r.results);
        setResults((prev) => ({ ...prev, ...Object.fromEntries(r.results.map((x) => [x.key, x])) }));
        setRows((prev) =>
          prev.map((row) => {
            const x = r.results.find((y) => y.key === row.key);
            if (!x || !x.status) return row;
            return {
              ...row,
              status: x.status,
              ...(x.status === "applied" ? { appliedAt: Date.now(), auto: false, newRecordUrl: x.newRecordUrl || row.newRecordUrl || null, outcome: x.outcome || row.outcome } : {}),
              ...(x.status === "rejected" ? { rejectedAt: Date.now() } : {}),
            };
          })
        );
      }
    } catch (e: any) {
      toast.error(e?.message || "Could not update the job changes.");
    } finally {
      setBusy((prev) => {
        const next = new Set(prev);
        keys.forEach((k) => next.delete(k));
        return next;
      });
    }
    const left = all.filter((x) => x.status && x.status !== "pending").length;
    if (left) {
      setData((d) => (d ? { ...d, pendingCount: Math.max(d.pendingCount - left, 0) } : d));
      // The badge counts the default scope (the team for admins), which every row here is part of.
      setPending((prev) => Math.max((prev || 0) - left, 0));
    }
    setSelected((prev) => {
      const next = new Set(prev);
      all.forEach((x) => x.ok && next.delete(x.key));
      return next;
    });
    const ok = all.filter((x) => x.ok).length;
    const failed = all.length - ok;
    if (all.length) {
      const done = action === "approve" ? "applied" : "rejected";
      if (!failed) toast.success(`${ok} job change${ok === 1 ? "" : "s"} ${done}`);
      else if (ok) toast.warn(`${ok} ${done}, ${failed} not: see each row's result`);
      else toast.error(all.length === 1 ? all[0].message || `Not ${done}` : `None ${done}: see each row's result`);
    }
  };

  const approve = (keys: string[]) => (keys.length > 1 ? setConfirmApprove(keys) : run(keys, "approve"));

  const columns: OuTableColumn<JobChangeRow>[] = [
    { key: "person", header: "Person", width: 170, sticky: true, render: (r) => <PersonCell row={r} crmName={crmName} /> },
    { key: "from", header: "From", width: 175, render: (r) => <RoleCell company={r.previousCompany} title={r.previousTitle} /> },
    { key: "to", header: "To", width: 175, render: (r) => <RoleCell company={r.newCompany} title={r.newTitle} /> },
    { key: "detectedAt", header: "Detected", width: 145, render: (r) => <Cell value={formatDate(r.detectedAt)} title={formatDateTime(r.detectedAt)} /> },
    { key: "status", header: "Status", width: 150, render: (r) => <StatusCell row={r} /> },
    {
      key: "actions",
      header: "Review",
      width: 180,
      render: (r) =>
        r.status === "pending" && mayAct(r) ? (
          <RowActions>
            <OuSecondaryButton size="small" loading={busy.has(r.key)} disabled={busy.has(r.key)} onClick={() => run([r.key], "approve")}>
              Approve
            </OuSecondaryButton>
            <OuSecondaryButton size="small" disabled={busy.has(r.key)} onClick={() => run([r.key], "reject")}>
              Reject
            </OuSecondaryButton>
          </RowActions>
        ) : null,
    },
    { key: "result", header: "Result", width: 280, render: (r) => <ResultCell row={r} result={results[r.key]} crmName={crmName} /> },
    { key: "owner", header: "Owner", width: 220, render: (r) => <OwnerCell row={r} /> },
  ];

  const description = autoApply
    ? `boring applies job changes on its own when the new ${crm === "hubspot" ? "company" : "account"} is already in ${crmName}. This is the log of what it changed, and the moves it could not apply (when the new ${crm === "hubspot" ? "company" : "account"} isn't in ${crmName} yet), which wait here for you to approve or reject.`
    : `People in your ${crmName} records who changed jobs on LinkedIn. Approve applies the change in ${crmName} under your job change settings, the same as confirming it in the extension; reject dismisses it so it doesn't come back.`;
  const noUndo = "An applied job change can't be undone here: the record has already been changed or replaced.";

  const filtered = !!from || !!to || status !== (autoApply ? "all" : "pending");
  const showTable = !loadError && !data?.planRequired && (isLoading || rows.length > 0);

  const body = (
    <Stack>
      <Toolbar>
        <ToolbarSide style={{ flex: "1 1 420px" }}>
          {isAdmin && (
            <SelectBox>
              <OuSelect
                aria-label="Whose job changes"
                value={data?.scope || "team"}
                onChange={(v) => {
                  setScope(v as JobChangeScope);
                  setPage(0);
                }}
                options={[
                  { value: "mine", label: "Mine" },
                  { value: "team", label: "Team" },
                ]}
              />
            </SelectBox>
          )}
          <SelectBox>
            <OuSelect
              aria-label="Status"
              value={status}
              onChange={(v) => {
                statusTouched.current = true;
                setStatus(v as StatusFilter);
                setPage(0);
              }}
              options={[
                { value: "pending", label: "Pending" },
                { value: "applied", label: "Applied" },
                { value: "rejected", label: "Rejected" },
                { value: "superseded", label: "Replaced" },
                { value: "all", label: "All" },
              ]}
            />
          </SelectBox>
          {crm === "hubspot" ? (
            <DateRangeBox>
              <HsDateRangeSelect
                prefix="Detected"
                aria-label="Detected between"
                from={from}
                to={to}
                onChange={(a, b) => {
                  setFrom(a);
                  setTo(b);
                  setPage(0);
                }}
              />
            </DateRangeBox>
          ) : (
            <>
            <DateInput
              type="date"
              aria-label="Detected from"
              title="Detected from"
              value={from}
              max={to || undefined}
              onChange={(e) => {
                setFrom(e.target.value);
                setPage(0);
              }}
            />
            <span style={{ color: OU.muted, fontFamily: OUTREACH_SANS, fontSize: 14 }}>to</span>
            <DateInput
              type="date"
              aria-label="Detected to"
              title="Detected to"
              value={to}
              min={from || undefined}
              onChange={(e) => {
                setTo(e.target.value);
                setPage(0);
              }}
            />
            {(from || to) && (
              <OuSecondaryButton
                size="small"
                onClick={() => {
                  setFrom("");
                  setTo("");
                  setPage(0);
                }}
              >
                Clear dates
              </OuSecondaryButton>
            )}
            </>
          )}
        </ToolbarSide>
        <ToolbarSide>
          <OuResultCount aria-live="polite">{data && !isLoading ? `${data.pendingCount.toLocaleString()} pending` : ""}</OuResultCount>
          <OuTooltip title="Refresh">
            <span>
              <OuIconButton aria-label="Refresh" onClick={load} disabled={isLoading}>
                <RefreshIcon />
              </OuIconButton>
            </span>
          </OuTooltip>
        </ToolbarSide>
      </Toolbar>

      {data && !isLoading && !data.planRequired && (
        <Caption>{crm === "hubspot" ? noUndo : `${data.scope === "mine" ? "The records you own." : "Every record in your organization."} ${noUndo}`}</Caption>
      )}

      {/* Always there while something can be reviewed, so ticking a row never moves the table. */}
      {crm !== "hubspot" && !isLoading && rows.some((r) => r.status === "pending" && mayAct(r)) && (
        <BulkBar role="region" aria-label="Selected job changes">
          <span>
            {selected.size ? `${selected.size} selected` : "Select job changes to approve or reject them together."}
            {selectedOther > 0 && <span style={{ color: OU.muted }}>{` (${selectedOther} already done or not yours)`}</span>}
          </span>
          <OuPrimaryButton size="small" disabled={!selectedActionable.length} onClick={() => approve(selectedActionable)}>
            Approve{selectedActionable.length ? ` ${selectedActionable.length}` : ""}
          </OuPrimaryButton>
          <OuSecondaryButton size="small" disabled={!selectedActionable.length} onClick={() => run(selectedActionable, "reject")}>
            Reject{selectedActionable.length ? ` ${selectedActionable.length}` : ""}
          </OuSecondaryButton>
          {selected.size > 0 && (
            <OuSecondaryButton size="small" onClick={() => setSelected(new Set())}>
              Clear selection
            </OuSecondaryButton>
          )}
        </BulkBar>
      )}

      {loadError && !isLoading && (
        <div>
          <OuNotice tone="danger">{loadError}</OuNotice>
          <div style={{ marginTop: 12 }}>
            <OuSecondaryButton onClick={load}>Try again</OuSecondaryButton>
          </div>
        </div>
      )}

      {data?.planRequired && !isLoading ? (
        <PlanUpgradePrompt feature="profileEnrichment" what="job changes" />
      ) : showTable && crm === "hubspot" ? (
        <div>
          <HsTableTopBar
            left={
              selected.size > 0 ? (
                <span className="hs-bulk">
                  <strong>{selected.size} selected</strong>
                  {selectedOther > 0 && <span>{`${selectedOther} already done or not yours`}</span>}
                  <button type="button" className="hs-btn hs-btn--transparent hs-focusable" disabled={!selectedActionable.length} onClick={() => approve(selectedActionable)}>
                    Approve{selectedActionable.length ? ` ${selectedActionable.length}` : ""}
                  </button>
                  <button type="button" className="hs-btn hs-btn--transparent hs-focusable" disabled={!selectedActionable.length} onClick={() => run(selectedActionable, "reject")}>
                    Reject{selectedActionable.length ? ` ${selectedActionable.length}` : ""}
                  </button>
                  <button type="button" className="hs-btn hs-btn--transparent hs-focusable" onClick={() => setSelected(new Set())}>
                    Clear selection
                  </button>
                </span>
              ) : (
                <Caption>{data && !isLoading && !data.planRequired ? `${data.scope === "mine" ? "The records you own." : "Every record in your organization."} Tick rows to approve or reject them together.` : ""}</Caption>
              )
            }
          />
          <div className="hs-table-frame">
            <OuTable<JobChangeRow>
              columns={columns}
              rows={isLoading ? [] : rows}
              getRowId={(r) => r.key}
              loading={isLoading}
              selection={{ selected, onChange: setSelected }}
              pageSize={PAGE_SIZE}
            />
          </div>
        </div>
      ) : showTable ? (
        <OuTable<JobChangeRow>
          columns={columns}
          rows={isLoading ? [] : rows}
          getRowId={(r) => r.key}
          loading={isLoading}
          selection={{ selected, onChange: setSelected }}
          pageSize={PAGE_SIZE}
        />
      ) : (
        !loadError && (
          <OuEmptyState
            icon={<MoveIcon />}
            title={filtered ? "No job changes match" : status === "pending" ? "No job changes waiting" : "No job changes yet"}
            hint={
              filtered
                ? "Change the status or the dates."
                : `When boring sees someone in ${crmName} change jobs on LinkedIn, it shows up here.`
            }
          />
        )
      )}

      {data && !isLoading && (page > 0 || data.hasMore) && (
        <Pager>
          <span>Page {page + 1}</span>
          <OuSecondaryButton size="small" disabled={page === 0} onClick={() => setPage((p) => Math.max(p - 1, 0))}>
            Previous
          </OuSecondaryButton>
          <OuSecondaryButton size="small" disabled={!data.hasMore} onClick={() => setPage((p) => p + 1)}>
            Next
          </OuSecondaryButton>
        </Pager>
      )}

      <Dialog
        open={!!confirmApprove}
        maxWidth={520}
        title={`Apply ${confirmApprove?.length || 0} job changes?`}
        onClose={() => setConfirmApprove(null)}
        primaryButtonProps={{
          children: "Apply",
          variant: ButtonVariants.brand,
          onClick: () => {
            const keys = confirmApprove || [];
            setConfirmApprove(null);
            run(keys, "approve");
          },
        }}
        secondaryButtonProps={{ children: "Cancel", variant: ButtonVariants.light, onClick: () => setConfirmApprove(null) }}
      >
        <Caption>
          {`boring changes each record in ${crmName} under your job change settings, as if each had been confirmed in the extension. ${noUndo}`}
        </Caption>
      </Dialog>
    </Stack>
  );

  if (crm === "hubspot") {
    return (
      <div className="hs-canvas">
        <div className="hs-jobchanges">
          <HubSpotSettingsPage title="Job changes" description={description}>
            <OuPage style={WIDE}>{body}</OuPage>
          </HubSpotSettingsPage>
        </div>
      </div>
    );
  }

  return (
    <Card className="p-4">
      <OuPage style={WIDE}>
        <div>
          <OuTitle>Job changes</OuTitle>
          <Caption style={{ marginTop: 4 }}>{description}</Caption>
        </div>
        {body}
      </OuPage>
    </Card>
  );
};

export default JobChanges;
